import Flash from '../../core/Flash';
import Text from '../../core/Text';
import View from '../../core/View';

type Issue = {
  readonly row: number;
  readonly kind: string;
  readonly field: string;
  readonly message: string;
};

type IssueRow = {
  row: number;
  fields: string[];
  messages: string[];
};

type Props = {
  totalCount: number;
  issues: readonly Issue[];
  notice?: string;
};

const ISSUE_GROUPS: { kind: string; title: string }[] = [
  { kind: 'error', title: '수정이 필요한 행' },
  { kind: 'not_found', title: '해당하는 배송 건이 없어 건너뛰는 행' },
  { kind: 'already_registered', title: '이미 송장이 등록되어 건너뛰는 행' },
];

// 한 행에 문제가 여러 개여도 엑셀의 행 하나로 묶는다.
const groupByRow = (issues: readonly Issue[]): IssueRow[] => {
  const rows = new Map<number, IssueRow>();
  issues.forEach(({ row, field, message }) => {
    const issueRow = rows.get(row) ?? { row, fields: [], messages: [] };
    if (field && !issueRow.fields.includes(field)) issueRow.fields.push(field);
    issueRow.messages.push(message);
    rows.set(row, issueRow);
  });
  return Array.from(rows.values()).sort((a, b) => a.row - b.row);
};

const DeliveryOrderXlsxResultView = ({ totalCount, issues, notice }: Props) => {
  const errorCount = groupByRow(issues.filter(({ kind }) => kind === 'error')).length;
  const skippedCount = groupByRow(issues.filter(({ kind }) => kind !== 'error')).length;

  return (
    <View sx={{ marginTop: 4 }}>
      <Flash variant={errorCount > 0 ? 'danger' : 'success'}>
        <Text sx={{ fontWeight: 'bold' }}>
          {`입력 ${totalCount}건 / 오류 ${errorCount}건`}
          {skippedCount > 0 ? ` / 건너뜀 ${skippedCount}건` : ''}
        </Text>
      </Flash>

      {notice ? (
        <View sx={{ marginTop: 2 }}>
          <Text sx={{ fontSize: 1, color: 'fg.subtle' }}>{notice}</Text>
        </View>
      ) : null}

      {ISSUE_GROUPS.map(({ kind, title }) => {
        const grouped = groupByRow(issues.filter((issue) => issue.kind === kind));
        if (grouped.length === 0) return null;

        return (
          <View key={kind} sx={{ marginTop: 4 }}>
            <Text sx={{ fontWeight: 'bold' }}>{`${title} (${grouped.length})`}</Text>
            <View
              sx={{
                marginTop: 2,
                maxHeight: 480,
                overflowY: 'auto',
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'border.default',
                borderRadius: 2,
                padding: 2,
              }}
            >
              {grouped.map(({ row, fields, messages }) => (
                <View key={row} sx={{ paddingY: 1 }}>
                  <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>
                    {fields.length > 0 ? `${row}행 — ${fields.join(', ')}` : `${row}행`}
                  </Text>
                  {messages.map((message, index) => (
                    <View key={index} sx={{ paddingLeft: 3 }}>
                      <Text sx={{ fontSize: 0, color: 'fg.subtle' }}>{message}</Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default DeliveryOrderXlsxResultView;
export type { Issue as DeliveryOrderXlsxIssueValue };
